/**
 * Email Service
 * Handles reading email data from Outlook via Office.js
 */

export interface EmailData {
  subject: string;
  body: string;
  bodyType: 'html' | 'text';
  senderEmail: string;
  senderName: string;
}

export class EmailService {
  /**
   * Check if Office.js is ready and we have a mailbox item
   */
  static isReady(): boolean {
    return !!(
      typeof Office !== 'undefined' &&
      Office.context &&
      Office.context.mailbox &&
      Office.context.mailbox.item
    );
  }

  /**
   * Get the current email item
   */
  private static getItem(): Office.MessageRead | null {
    if (!this.isReady()) return null;
    return Office.context.mailbox.item as Office.MessageRead;
  }

  /**
   * Get email subject
   */
  static getSubject(): string {
    const item = this.getItem();
    if (!item) return '';
    return item.subject || '';
  }

  /**
   * Get sender email address
   */
  static getSenderEmail(): string {
    const item = this.getItem();
    if (!item || !item.from) return '';
    return item.from.emailAddress || '';
  }

  /**
   * Get sender display name
   */
  static getSenderName(): string {
    const item = this.getItem();
    if (!item || !item.from) return '';
    return item.from.displayName || '';
  }

  /**
   * Get email body asynchronously
   */
  static getBody(): Promise<{ body: string; type: 'html' | 'text' }> {
    return new Promise((resolve, reject) => {
      const item = this.getItem();
      if (!item) {
        reject(new Error('No email item available'));
        return;
      }

      // Try to get HTML body first
      item.body.getAsync(
        Office.CoercionType.Html,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve({ body: result.value, type: 'html' });
          } else {
            // Fall back to text
            item.body.getAsync(
              Office.CoercionType.Text,
              (textResult) => {
                if (textResult.status === Office.AsyncResultStatus.Succeeded) {
                  resolve({ body: textResult.value, type: 'text' });
                } else {
                  reject(new Error(textResult.error?.message || 'Failed to get email body'));
                }
              }
            );
          }
        }
      );
    });
  }

  /**
   * Get all email data at once
   */
  static async getEmailData(): Promise<EmailData> {
    const subject = this.getSubject();
    const senderEmail = this.getSenderEmail();
    const senderName = this.getSenderName();
    const { body, type } = await this.getBody();

    return {
      subject,
      body,
      bodyType: type,
      senderEmail,
      senderName
    };
  }

  /**
   * Clean HTML body for display
   * Removes potentially problematic elements
   */
  static cleanHtmlBody(html: string): string {
    // Create a temporary div to parse HTML
    const temp = document.createElement('div');
    temp.innerHTML = html;

    // Remove script and style tags
    const scripts = temp.getElementsByTagName('script');
    const styles = temp.getElementsByTagName('style');

    while (scripts.length > 0) {
      scripts[0].parentNode?.removeChild(scripts[0]);
    }
    while (styles.length > 0) {
      styles[0].parentNode?.removeChild(styles[0]);
    }

    return temp.innerHTML;
  }

  /**
   * Convert text to simple HTML (preserve line breaks)
   */
  static textToHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br>');
  }

  /**
   * Register for item changed events (when user selects different email)
   */
  static onItemChanged(callback: () => void): void {
    if (!Office.context?.mailbox) return;

    Office.context.mailbox.addHandlerAsync(
      Office.EventType.ItemChanged,
      callback
    );
  }
}
